import { useMemo } from 'react';

interface GridLayout {
  cols: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
  shouldScroll: boolean;
}

interface ContainerSize {
  width: number;
  height: number;
}

/**
 * Умный хук для вычисления адаптивной сетки видео
 * Учитывает размер контейнера, количество участников и соотношение сторон
 */
 export const useAdaptiveGrid = (participantCount: number, containerSize: ContainerSize): GridLayout => {
  const layout = useMemo(() => {
    const isMobile = containerSize.width < 640;
    const gridPadding = isMobile ? 16 : 32;
    const gapSize = isMobile ? 8 : 16;

    // Используем размеры контейнера с учетом padding
    const availableWidth = Math.max(containerSize.width - gridPadding, 200);
    const availableHeight = Math.max(containerSize.height - gridPadding, 200);

    // Минимальные и максимальные размеры плитки
    const MIN_TILE_WIDTH = isMobile ? 120 : 200;
    const MAX_TILE_WIDTH = 500;
    const ASPECT_RATIO = 16 / 9;

    // Определяем максимальное количество плиток на экране без скролла
    let maxVisibleTiles: number;
    
    if (isMobile) {
      // Мобильные: до 6 плиток
      maxVisibleTiles = 6;
    } else if (containerSize.width < 1024) {
      // Планшеты: 4-6 плиток
      maxVisibleTiles = 6;
    } else if (containerSize.width < 1920) {
      // Десктоп: 9-12 плиток
      maxVisibleTiles = 12;
    } else {
      // Большие экраны: до 24 плиток
      maxVisibleTiles = 24;
    }

    // Определяем нужно ли скроллить
    const shouldScroll = participantCount > maxVisibleTiles;
    const displayCount = shouldScroll ? maxVisibleTiles : participantCount;

    // Вычисляем оптимальное количество колонок и рядов
    let cols: number;
    let rows: number;

    if (displayCount === 1) {
      cols = 1;
      rows = 1;
    } else if (displayCount === 2) {
      cols = isMobile ? 2 : 2;
      rows = 1;
    } else if (displayCount <= 4) {
      cols = 2;
      rows = Math.ceil(displayCount / 2);
    } else if (displayCount <= 6) {
      cols = isMobile ? 2 : 3;
      rows = Math.ceil(displayCount / cols);
    } else if (displayCount <= 9) {
      cols = isMobile ? 2 : containerSize.width < 1024 ? 3 : 3;
      rows = Math.ceil(displayCount / cols);
    } else {
      // Для большого количества участников
      if (isMobile) {
        cols = 2;
      } else if (containerSize.width < 1024) {
        cols = 3;
      } else if (containerSize.width < 1920) {
        cols = 4;
      } else {
        cols = 6;
      }
      rows = Math.ceil(displayCount / cols);
    }

    // Вычисляем размеры плитки с учетом gap между плитками
    const totalGapWidth = (cols - 1) * gapSize;
    const totalGapHeight = (rows - 1) * gapSize;

    let tileWidth = (availableWidth - totalGapWidth) / cols;
    let tileHeight = tileWidth / ASPECT_RATIO;

    // Проверяем не превышаем ли высоту
    const totalHeight = tileHeight * rows + totalGapHeight;
    if (totalHeight > availableHeight && !shouldScroll) {
      // Пересчитываем на основе высоты
      tileHeight = (availableHeight - totalGapHeight) / rows;
      tileWidth = tileHeight * ASPECT_RATIO;
    }

    // Ограничиваем размеры
    tileWidth = Math.max(MIN_TILE_WIDTH, Math.min(MAX_TILE_WIDTH, tileWidth));
    tileHeight = tileWidth / ASPECT_RATIO;

    return {
      cols,
      rows,
      tileWidth: Math.floor(tileWidth),
      tileHeight: Math.floor(tileHeight),
      shouldScroll,
    };
  }, [participantCount, containerSize]);

  return layout;
};
